import React, { useState, useEffect, useCallback } from "react";
import {
  FlaskConical, Play, RotateCcw, TrendingUp, Banknote, Wallet, Target, Building2, Lightbulb,
} from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input } from "../../lib/ui";
import { useT } from "../../lib/i18n";
import { useToast } from "../../lib/toast";
import api from "../../lib/api";
import { rp, pct, PeriodPicker, BranchFilter } from "../../components/finance";

const DEFAULTS = {
  revenue_change_pct: 0, raw_material_change_pct: 0, labor_change_pct: 0,
  occupancy_change_pct: 0, marketing_change_pct: 0, other_opex_change_pct: 0,
};
const DEFAULT_BRANCH = {
  enabled: false, monthly_revenue: 0, cogs_pct: "", monthly_payroll: 0,
  monthly_rent: 0, monthly_other_opex: 0, setup_cost: 0,
};

function Slider({ label, value, onChange, testid }) {
  const tone = value > 0 ? "text-success-text" : value < 0 ? "text-danger-text" : "text-ink-soft";
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm font-medium text-ink-soft">{label}</span>
        <span className={`font-heading text-sm font-bold ${tone}`} data-testid={`${testid}-value`}>{value > 0 ? "+" : ""}{value}%</span>
      </div>
      <input
        type="range" min={-50} max={50} step={1} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-stone-200 accent-primary"
      />
    </div>
  );
}

function ImpactCard({ icon: Icon, label, baseline, adjusted, delta, deltaSub, positiveGood = true, testid }) {
  const { t } = useT();
  const improved = positiveGood ? delta >= 0 : delta <= 0;
  return (
    <Card data-testid={testid}>
      <div className="mb-2 flex items-center gap-2 text-ink-soft">
        <Icon size={16} /> <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <p className={`font-heading text-2xl font-bold ${improved ? "text-success-text" : "text-danger-text"}`}>
        {delta >= 0 ? "+" : ""}{rp(delta)}
      </p>
      {deltaSub != null && <p className="text-xs text-ink-mute">{deltaSub}</p>}
      <div className="mt-2 flex items-center justify-between border-t border-line pt-2 text-xs text-ink-soft">
        <span>{t("Baseline")}: <b className="text-ink">{rp(baseline)}</b></span>
        <span>{t("Adjusted")}: <b className="text-ink">{rp(adjusted)}</b></span>
      </div>
    </Card>
  );
}

export default function ScenarioSimulator() {
  const { t } = useT();
  const toast = useToast();
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [branchId, setBranchId] = useState("");
  const [params, setParams] = useState(DEFAULTS);
  const [branch, setBranch] = useState(DEFAULT_BRANCH);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const simulate = useCallback(async (p, prm, br, bid) => {
    setLoading(true);
    try {
      const payload = {
        period: p, branch_id: bid || undefined, ...prm,
        new_branch: br.enabled ? {
          enabled: true,
          monthly_revenue: Number(br.monthly_revenue) || 0,
          cogs_pct: br.cogs_pct === "" ? null : Number(br.cogs_pct),
          monthly_payroll: Number(br.monthly_payroll) || 0,
          monthly_rent: Number(br.monthly_rent) || 0,
          monthly_other_opex: Number(br.monthly_other_opex) || 0,
          setup_cost: Number(br.setup_cost) || 0,
        } : { enabled: false },
      };
      const { data } = await api.post("/finance/scenario-simulator", payload);
      setResult(data);
    } catch { toast.error(t("Failed")); }
    finally { setLoading(false); }
  }, [t, toast]);

  // auto-run baseline on mount + when period/branch changes
  useEffect(() => { simulate(period, params, branch, branchId); /* eslint-disable-next-line */ }, [period, branchId]);

  const setParam = (k, v) => setParams((s) => ({ ...s, [k]: v }));
  const setBranchField = (k, v) => setBranch((s) => ({ ...s, [k]: v }));
  const reset = () => { setParams(DEFAULTS); setBranch(DEFAULT_BRANCH); simulate(period, DEFAULTS, DEFAULT_BRANCH, branchId); };

  const imp = result?.impact;
  const be = result?.break_even;

  return (
    <div data-testid="scenario-simulator-page">
      <PageHeader title={t("Scenario Simulator")} subtitle={t("Model business decisions before you make them")} icon={FlaskConical}>
        <BranchFilter value={branchId} onChange={setBranchId} testid="scenario-branch-filter" />
        <PeriodPicker value={period} onChange={setPeriod} />
      </PageHeader>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Controls */}
        <Card className="lg:col-span-1 self-start" data-testid="scenario-controls">
          <h3 className="mb-1 font-heading font-bold text-ink">{t("Adjustments")}</h3>
          <p className="mb-4 text-xs text-ink-mute">{t("Baseline period")}: {period}{result?.branch_name ? ` · ${result.branch_name}` : ` · ${t("Semua Cabang")}`}</p>
          <div className="space-y-4">
            <Slider label={t("Revenue Change")} value={params.revenue_change_pct} onChange={(v) => setParam("revenue_change_pct", v)} testid="slider-revenue" />
            <Slider label={t("Raw Material Cost Change")} value={params.raw_material_change_pct} onChange={(v) => setParam("raw_material_change_pct", v)} testid="slider-rawmat" />
            <Slider label={t("Labor Cost Change")} value={params.labor_change_pct} onChange={(v) => setParam("labor_change_pct", v)} testid="slider-labor" />
            <Slider label={t("Occupancy Cost Change")} value={params.occupancy_change_pct} onChange={(v) => setParam("occupancy_change_pct", v)} testid="slider-occupancy" />
            <Slider label={t("Marketing Cost Change")} value={params.marketing_change_pct} onChange={(v) => setParam("marketing_change_pct", v)} testid="slider-marketing" />
            <Slider label={t("Other OPEX Change")} value={params.other_opex_change_pct} onChange={(v) => setParam("other_opex_change_pct", v)} testid="slider-otheropex" />
          </div>

          {/* New branch */}
          <div className="mt-5 rounded-xl border border-line p-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-ink">
              <input type="checkbox" checked={branch.enabled} onChange={(e) => setBranchField("enabled", e.target.checked)} className="h-4 w-4 accent-primary" data-testid="newbranch-toggle" />
              <Building2 size={15} /> {t("New Branch Simulation")}
            </label>
            {branch.enabled && (
              <div className="mt-3 space-y-2" data-testid="newbranch-fields">
                <Input label={t("Monthly Revenue")} type="number" value={branch.monthly_revenue} onChange={(e) => setBranchField("monthly_revenue", e.target.value)} data-testid="newbranch-revenue" />
                <Input label={t("COGS %")} type="number" placeholder="auto" value={branch.cogs_pct} onChange={(e) => setBranchField("cogs_pct", e.target.value)} data-testid="newbranch-cogspct" />
                <Input label={t("Monthly Payroll")} type="number" value={branch.monthly_payroll} onChange={(e) => setBranchField("monthly_payroll", e.target.value)} data-testid="newbranch-payroll" />
                <Input label={t("Monthly Rent")} type="number" value={branch.monthly_rent} onChange={(e) => setBranchField("monthly_rent", e.target.value)} data-testid="newbranch-rent" />
                <Input label={t("Monthly Other OPEX")} type="number" value={branch.monthly_other_opex} onChange={(e) => setBranchField("monthly_other_opex", e.target.value)} data-testid="newbranch-other" />
                <Input label={t("One-time Setup Cost")} type="number" value={branch.setup_cost} onChange={(e) => setBranchField("setup_cost", e.target.value)} data-testid="newbranch-setup" />
              </div>
            )}
          </div>

          <div className="mt-4 flex gap-2">
            <Button className="flex-1" onClick={() => simulate(period, params, branch, branchId)} loading={loading} data-testid="run-simulation"><Play size={15} /> {t("Run Simulation")}</Button>
            <Button variant="outline" onClick={reset} data-testid="reset-simulation"><RotateCcw size={15} /> {t("Reset")}</Button>
          </div>
        </Card>

        {/* Output */}
        <div className="lg:col-span-2">
          {!result ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <ImpactCard icon={TrendingUp} label={t("Revenue Impact")} baseline={imp.revenue.baseline} adjusted={imp.revenue.adjusted} delta={imp.revenue.delta} deltaSub={imp.revenue.delta_pct != null ? pct(imp.revenue.delta_pct) : null} testid="impact-revenue" />
                <ImpactCard icon={Banknote} label={t("Profit Impact")} baseline={imp.profit.baseline} adjusted={imp.profit.adjusted} delta={imp.profit.delta} deltaSub={imp.profit.delta >= 0 ? t("improvement") : t("decline")} testid="impact-profit" />
                <ImpactCard icon={Wallet} label={t("Cash Flow Impact")} baseline={imp.cash_flow.baseline_monthly} adjusted={imp.cash_flow.baseline_monthly + imp.cash_flow.monthly_delta} delta={imp.cash_flow.monthly_delta} deltaSub={`${t("per month")}${imp.cash_flow.one_time_setup_cost ? ` · ${t("one-time")} ${rp(imp.cash_flow.one_time_setup_cost)}` : ""}`} testid="impact-cashflow" />
              </div>

              {/* Break-even */}
              <Card data-testid="break-even">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="flex items-center gap-2 font-heading font-bold text-ink"><Target size={18} /> {t("Break-even Estimate")}</h3>
                  <Badge tone={be.is_profitable ? "success" : "danger"}>{be.is_profitable ? t("Profitable") : t("Not yet profitable")}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                  <div><p className="text-xs text-ink-mute">{t("Break-even Revenue")}</p><p className="font-heading font-bold text-ink">{be.break_even_revenue != null ? rp(be.break_even_revenue) : "—"}</p></div>
                  <div><p className="text-xs text-ink-mute">{t("Monthly Fixed Costs")}</p><p className="font-heading font-bold text-ink">{rp(be.monthly_fixed_costs)}</p></div>
                  <div><p className="text-xs text-ink-mute">{t("Contribution Margin")}</p><p className="font-heading font-bold text-ink">{be.contribution_margin_ratio != null ? pct(be.contribution_margin_ratio * 100) : "—"}</p></div>
                  <div><p className="text-xs text-ink-mute">{t("Margin of Safety")}</p><p className={`font-heading font-bold ${(be.margin_of_safety_pct || 0) >= 0 ? "text-success-text" : "text-danger-text"}`}>{be.margin_of_safety_pct != null ? pct(be.margin_of_safety_pct) : "—"}</p></div>
                </div>
                {be.new_branch_payback_months != null && (
                  <div className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800" data-testid="newbranch-payback">
                    {t("New Branch Payback")}: <b>{be.new_branch_payback_months} {t("months")}</b>
                  </div>
                )}
                <div className="mt-3 grid grid-cols-2 gap-3 border-t border-line pt-3 text-xs">
                  <span className="text-ink-soft">{t("First Month Net")}: <b className={imp.cash_flow.first_month_net >= 0 ? "text-success-text" : "text-danger-text"}>{rp(imp.cash_flow.first_month_net)}</b></span>
                  <span className="text-ink-soft text-right">{t("Annual Impact")}: <b className={imp.cash_flow.annual_delta >= 0 ? "text-success-text" : "text-danger-text"}>{rp(imp.cash_flow.annual_delta)}</b></span>
                </div>
              </Card>

              {/* Baseline vs Adjusted P&L */}
              <Card data-testid="scenario-comparison">
                <h3 className="mb-3 font-heading font-bold text-ink">{t("Comparison")}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
                      <tr><th className="py-2 pr-3" /><th className="py-2 pr-3 text-right">{t("Baseline")}</th><th className="py-2 pr-3 text-right">{t("Adjusted")}</th><th className="py-2 pr-3 text-right">{t("Delta")}</th></tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {[
                        ["Revenue", "revenue"], ["COGS", "cogs"], ["Gross Profit", "gross_profit"],
                        ["Labor", "labor"], ["Occupancy", "occupancy"], ["Net Profit", "net_profit"],
                      ].map(([label, key]) => {
                        const b = result.baseline[key], a = result.adjusted[key], d = Math.round(a - b);
                        const isNet = key === "net_profit";
                        return (
                          <tr key={key} className={isNet ? "font-semibold" : ""}>
                            <td className="py-2 pr-3 text-ink-soft">{t(label)}</td>
                            <td className="py-2 pr-3 text-right text-ink-soft">{rp(b)}</td>
                            <td className="py-2 pr-3 text-right text-ink">{rp(a)}</td>
                            <td className={`py-2 pr-3 text-right ${d === 0 ? "text-ink-mute" : d > 0 ? "text-success-text" : "text-danger-text"}`}>{d >= 0 ? "+" : ""}{rp(d)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Assumptions */}
              <Card className="bg-stone-50" data-testid="scenario-assumptions">
                <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-soft"><Lightbulb size={14} /> {t("Assumptions")}</p>
                <ul className="list-disc space-y-1 pl-5 text-xs text-ink-mute">
                  {result.assumptions.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
